import { z } from "zod";

/**
 * CSV-Import für Aufträge und Artikel: robuster Parser (Trennzeichen erkannt, Anführungszeichen, Zeilenumbrüche in
 * Feldern), Vorlagen mit Spaltenzuordnung und Prüfung je Zeile. Gleich für manuellen Upload und SFTP-Abholung.
 */

// ── Lesen ─────────────────────────────────────────────────────────────

/** Bytes → Text: UTF-8 (mit/ohne BOM), sonst Windows-1252 (typischer Excel-Export) */
export function decodeCsv(bytes: Uint8Array): string {
  try {
    const t = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return t.charCodeAt(0) === 0xfeff ? t.slice(1) : t;
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
}

/** Trennzeichen aus der ersten Zeile: das häufigste von ; , Tab | */
export function detectDelimiter(text: string): string {
  const first = text.split(/\r?\n/, 1)[0] ?? "";
  const best = [";", ",", "\t", "|"].map((d) => ({ d, n: first.split(d).length - 1 })).sort((a, b) => b.n - a.n)[0]!;
  return best.n > 0 ? best.d : ";";
}

/** RFC-4180-Parser: Anführungszeichen, verdoppelte "" und Zeilenumbrüche in Feldern; leere Zeilen entfallen */
export function parseCsv(text: string, delimiter = detectDelimiter(text)): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else quoted = false;
      } else field += c;
      continue;
    }
    if (c === '"' && field === "") quoted = true;
    else if (c === delimiter) { row.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(field); field = "";
      if (row.some((f) => f.trim() !== "")) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some((f) => f.trim() !== "")) rows.push(row);
  return rows.map((r) => r.map((f) => f.trim()));
}

/** CSV schreiben (für Rückmeldungen): Felder mit Trennzeichen/Anführungszeichen/Umbruch werden gequotet */
export function toCsv(rows: (string | number | null | undefined)[][], delimiter = ";"): string {
  const esc = (v: string | number | null | undefined) => {
    const s = v == null ? "" : String(v);
    return /["\r\n]/.test(s) || s.includes(delimiter) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return rows.map((r) => r.map(esc).join(delimiter)).join("\r\n") + "\r\n";
}

// ── Felder & Vorlagen ─────────────────────────────────────────────────

export const ORDER_FIELDS = {
  orderNumber: { label: "Auftragsnummer", required: true, hint: "gleiche Nummer = ein Auftrag mit mehreren Positionen" },
  sku: { label: "SKU", required: true },
  quantity: { label: "Menge", required: true },
  lastName: { label: "Nachname", required: true },
  firstName: { label: "Vorname", required: false },
  company: { label: "Firma", required: false },
  street: { label: "Straße (inkl. Hausnummer)", required: true },
  houseNumber: { label: "Hausnummer", required: false },
  addition: { label: "Adresszusatz", required: false },
  zip: { label: "PLZ", required: true },
  city: { label: "Ort", required: true },
  country: { label: "Land (ISO, z. B. DE)", required: false },
  email: { label: "E-Mail", required: false },
  phone: { label: "Telefon", required: false },
  shippingMethod: { label: "Versandart (Name wie im Shop)", required: false },
  /** nur als Standardwert: Lager-Versandart direkt (statt Namensabgleich über die Versandarten-Zuordnung) */
  shippingMethodId: { label: "Lager-Versandart", required: false, defaultOnly: true },
  unitPrice: { label: "Einzelpreis", required: false },
  note: { label: "Notiz", required: false },
  orderDate: { label: "Bestelldatum", required: false },
} as const;
export type OrderField = keyof typeof ORDER_FIELDS;

export const PRODUCT_FIELDS = {
  sku: { label: "SKU", required: true },
  name: { label: "Bezeichnung", required: true },
  gtin: { label: "EAN / GTIN", required: false },
  manufacturer: { label: "Hersteller", required: false },
  grossWeightKg: { label: "Gewicht (kg)", required: false },
  netWeightKg: { label: "Nettogewicht (kg)", required: false },
  lengthCm: { label: "Länge (cm)", required: false },
  widthCm: { label: "Breite (cm)", required: false },
  heightCm: { label: "Höhe (cm)", required: false },
  hsCode: { label: "Zolltarifnummer", required: false },
  countryOfOrigin: { label: "Ursprungsland (ISO)", required: false },
  customsValue: { label: "Zollwert (€)", required: false },
  batchManaged: { label: "Chargenpflichtig (ja/nein)", required: false },
  bestBeforeManaged: { label: "MHD-pflichtig (ja/nein)", required: false },
  description: { label: "Beschreibung", required: false },
} as const;
export type ProductField = keyof typeof PRODUCT_FIELDS;

export const importTemplateSchema = z.object({
  kind: z.enum(["orders", "products"]),
  name: z.string().trim().min(1).max(80),
  /** leer = automatisch erkennen */
  delimiter: z.enum([";", ",", "\t", "|"]).nullable().default(null),
  hasHeader: z.boolean().default(true),
  /** Feld → Spalte (Überschrift bei hasHeader, sonst 1-basierte Spaltennummer als Text) */
  mapping: z.record(z.string(), z.string().trim().min(1)).default({}),
  /** Standardwerte, z. B. { country: "DE" } */
  defaults: z.record(z.string(), z.string()).default({}),
});
export type ImportTemplate = z.infer<typeof importTemplateSchema>;

/** Unsere Vorlage: Spaltenüberschriften = Feldnamen auf Deutsch, Semikolon (Excel-freundlich) */
export function templateHeader(kind: "orders" | "products"): string[] {
  return Object.values(kind === "orders" ? ORDER_FIELDS : PRODUCT_FIELDS).filter((f) => !("defaultOnly" in f)).map((f) => f.label);
}
export function defaultTemplate(kind: "orders" | "products"): ImportTemplate {
  const fields = kind === "orders" ? ORDER_FIELDS : PRODUCT_FIELDS;
  return { kind, name: "Logiful-Vorlage", delimiter: ";", hasHeader: true, mapping: Object.fromEntries(Object.entries(fields).filter(([, f]) => !("defaultOnly" in f)).map(([k, f]) => [k, f.label])), defaults: kind === "orders" ? { country: "DE" } : {} };
}

/**
 * Spaltenzuordnung automatisch vorschlagen (für fremde Exporte): Überschrift ähnelt Feldname/Label/üblichen Synonymen
 */
const SYNONYMS: Record<string, string[]> = {
  orderNumber: ["auftragsnummer", "auftragsnr", "auftrags-nr", "bestellnummer", "bestellnr", "bestell-nr", "order no", "order-no", "order", "ordernumber", "order number", "order id", "belegnummer", "auftrag"],
  sku: ["sku", "artikelnummer", "artikelnr", "artnr", "art.-nr.", "artikel-nr", "item", "product sku"],
  quantity: ["menge", "anzahl", "qty", "quantity", "stück"],
  lastName: ["nachname", "name", "last name", "lastname", "empfänger"],
  firstName: ["vorname", "first name", "firstname"],
  company: ["firma", "company", "unternehmen"],
  street: ["straße", "strasse", "street", "adresse", "address", "address1"],
  houseNumber: ["hausnummer", "hausnr", "house number"],
  addition: ["adresszusatz", "zusatz", "address2"],
  zip: ["plz", "postleitzahl", "zip", "postcode", "postal code"],
  city: ["ort", "stadt", "city"],
  country: ["land", "country", "ländercode", "country code"],
  email: ["e-mail", "email", "mail"],
  phone: ["telefon", "phone", "tel"],
  shippingMethod: ["versandart", "shipping", "shipping method", "versand"],
  unitPrice: ["preis", "einzelpreis", "price", "unit price"],
  note: ["notiz", "bemerkung", "note", "kommentar"],
  orderDate: ["datum", "bestelldatum", "order date", "date"],
  name: ["bezeichnung", "artikelname", "name", "titel", "title"],
  gtin: ["ean", "gtin", "barcode"],
  manufacturer: ["hersteller", "marke", "brand", "vendor"],
  grossWeightKg: ["gewicht", "gewicht (kg)", "weight", "bruttogewicht", "bruttogewicht (kg)", "gross weight"],
  netWeightKg: ["nettogewicht", "net weight"],
  lengthCm: ["länge", "laenge", "length"],
  widthCm: ["breite", "width"],
  heightCm: ["höhe", "hoehe", "height"],
  hsCode: ["zolltarifnummer", "hs-code", "hs code", "taric"],
  countryOfOrigin: ["ursprungsland", "herkunftsland", "country of origin"],
  customsValue: ["zollwert", "warenwert", "customs value"],
  batchManaged: ["chargenpflichtig", "charge"],
  bestBeforeManaged: ["mhd-pflichtig", "mhd"],
  description: ["beschreibung", "description"],
};
export function suggestMapping(kind: "orders" | "products", header: string[]): Record<string, string> {
  const fields = kind === "orders" ? ORDER_FIELDS : PRODUCT_FIELDS;
  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
  const out: Record<string, string> = {};
  const used = new Set<string>();
  for (const [key, f] of Object.entries(fields)) {
    if ("defaultOnly" in f) continue;
    const wanted = [norm(f.label), norm(key), ...(SYNONYMS[key] ?? [])];
    const hit = header.find((h) => !used.has(h) && wanted.includes(norm(h)));
    if (hit) { out[key] = hit; used.add(hit); }
  }
  return out;
}

// ── Anwenden ──────────────────────────────────────────────────────────

/** Tabelle + Vorlage → Datensätze je Zeile (Feld → Wert); Zeilennummer wie in Excel (Überschrift = 1) */
export function applyTemplate(rows: string[][], t: Pick<ImportTemplate, "hasHeader" | "mapping" | "defaults"> & { kind?: "orders" | "products" }): { header: string[]; records: { line: number; values: Record<string, string> }[]; missingColumns: string[]; ignoredColumns: string[] } {
  const header = t.hasHeader ? (rows[0] ?? []) : [];
  const body = t.hasHeader ? rows.slice(1) : rows;
  const colOf = (spec: string) => (t.hasHeader ? header.findIndex((h) => h.toLowerCase() === spec.toLowerCase()) : Number(spec) - 1);
  const cols = Object.fromEntries(Object.entries(t.mapping).map(([field, spec]) => [field, colOf(spec)]));
  // fehlende Spalten: Pflichtfelder blockieren den Import, optionale werden nur ausgelassen
  const fields: Record<string, { required: boolean }> = t.kind === "products" ? PRODUCT_FIELDS : t.kind === "orders" ? ORDER_FIELDS : {};
  const absent = Object.entries(cols).filter(([, i]) => i < 0).map(([f]) => f);
  const missingColumns = absent.filter((f) => !t.kind || fields[f]?.required).map((f) => t.mapping[f]!);
  const ignoredColumns = absent.filter((f) => t.kind && !fields[f]?.required).map((f) => t.mapping[f]!);
  const records = body.map((r, i) => {
    const values: Record<string, string> = { ...t.defaults };
    for (const [field, idx] of Object.entries(cols)) if (idx >= 0 && (r[idx] ?? "") !== "") values[field] = r[idx]!;
    return { line: i + (t.hasHeader ? 2 : 1), values };
  });
  return { header, records, missingColumns, ignoredColumns };
}

/** Zahl in deutscher oder englischer Schreibweise ("1.234,50" / "1234.50") */
export function parseNumber(s: string | undefined): number | null {
  if (!s) return null;
  const t = s.replace(/[€\s]/g, "");
  const normalized = /,\d{1,3}$/.test(t) ? t.replace(/\./g, "").replace(",", ".") : t.replace(/,/g, "");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}
const parseBool = (s: string | undefined) => (s ? ["ja", "j", "yes", "y", "true", "1", "x"].includes(s.trim().toLowerCase()) : undefined);
/** Datum "31.12.2026", "2026-12-31" (auch mit Uhrzeit) */
export function parseDate(s: string | undefined): string | null {
  if (!s) return null;
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{2,4})/.exec(s.trim());
  if (de) {
    const y = de[3]!.length === 2 ? `20${de[3]}` : de[3]!;
    return new Date(`${y}-${de[2]!.padStart(2, "0")}-${de[1]!.padStart(2, "0")}T12:00:00Z`).toISOString();
  }
  const d = new Date(s.trim());
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export interface CsvIssue {
  line: number;
  message: string;
}

export interface CsvOrder {
  orderNumber: string;
  placedAt: string | null;
  shippingMethod: string | null;
  /** Lager-Versandart (Standardwert der Vorlage) – gilt, wenn die Datei keine Versandart nennt */
  shippingMethodId: string | null;
  note: string | null;
  address: { firstName?: string; lastName: string; company?: string; street: string; houseNumber?: string; addition?: string; zip: string; city: string; country: string; email?: string; phone?: string };
  lines: { sku: string; quantity: number; unitPriceMinor: number | null }[];
  firstLine: number;
}

/** Datensätze → Aufträge (gleiche Auftragsnummer = ein Auftrag); Fehler je Zeile, fehlerhafte Aufträge entfallen ganz */
export function toOrders(records: { line: number; values: Record<string, string> }[]): { orders: CsvOrder[]; issues: CsvIssue[] } {
  const issues: CsvIssue[] = [];
  const byNumber = new Map<string, CsvOrder>();
  const broken = new Set<string>();
  for (const { line, values: v } of records) {
    const missing = (Object.entries(ORDER_FIELDS) as [OrderField, { label: string; required: boolean }][]).filter(([k, f]) => f.required && !v[k]?.trim()).map(([, f]) => f.label);
    const num = v.orderNumber?.trim();
    if (missing.length) {
      issues.push({ line, message: `Pflichtfeld fehlt: ${missing.join(", ")}` });
      if (num) broken.add(num);
      continue;
    }
    const qty = parseNumber(v.quantity);
    if (!qty || qty <= 0 || !Number.isInteger(qty)) {
      issues.push({ line, message: `Menge „${v.quantity}“ ist keine ganze Zahl > 0` });
      broken.add(num!);
      continue;
    }
    const country = (v.country ?? "DE").trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(country)) {
      issues.push({ line, message: `Land „${v.country}“ – bitte als ISO-Code, z. B. DE, AT, CH` });
      broken.add(num!);
      continue;
    }
    const price = parseNumber(v.unitPrice);
    const lineItem = { sku: v.sku!.trim(), quantity: qty, unitPriceMinor: price == null ? null : Math.round(price * 100) };
    const existing = byNumber.get(num!);
    if (existing) {
      existing.lines.push(lineItem);
      continue;
    }
    byNumber.set(num!, {
      orderNumber: num!,
      placedAt: parseDate(v.orderDate),
      shippingMethod: v.shippingMethod?.trim() || null,
      shippingMethodId: !v.shippingMethod?.trim() && v.shippingMethodId?.trim() ? v.shippingMethodId.trim() : null,
      note: v.note?.trim() || null,
      address: {
        ...(v.firstName ? { firstName: v.firstName } : {}),
        lastName: v.lastName!,
        ...(v.company ? { company: v.company } : {}),
        street: v.street!,
        ...(v.houseNumber ? { houseNumber: v.houseNumber } : {}),
        ...(v.addition ? { addition: v.addition } : {}),
        zip: v.zip!,
        city: v.city!,
        country,
        ...(v.email ? { email: v.email } : {}),
        ...(v.phone ? { phone: v.phone } : {}),
      },
      lines: [lineItem],
      firstLine: line,
    });
  }
  for (const n of broken) if (byNumber.delete(n)) issues.push({ line: 0, message: `Auftrag ${n} wird wegen fehlerhafter Zeilen nicht importiert` });
  return { orders: [...byNumber.values()], issues };
}

export interface CsvProduct {
  line: number;
  sku: string;
  fields: Record<string, string | number | boolean>;
}

/** Datensätze → Artikel (nur gesetzte Felder werden übernommen – leere Zellen überschreiben nichts) */
export function toProducts(records: { line: number; values: Record<string, string> }[]): { products: CsvProduct[]; issues: CsvIssue[] } {
  const issues: CsvIssue[] = [];
  const out = new Map<string, CsvProduct>();
  for (const { line, values: v } of records) {
    const sku = v.sku?.trim();
    if (!sku) { issues.push({ line, message: "SKU fehlt" }); continue; }
    if (out.has(sku)) { issues.push({ line, message: `SKU ${sku} doppelt – nur die erste Zeile zählt` }); continue; }
    const f: Record<string, string | number | boolean> = {};
    if (v.name) f.name = v.name;
    for (const k of ["gtin", "manufacturer", "hsCode", "description"] as const) if (v[k]) f[k] = v[k]!;
    if (v.countryOfOrigin) f.countryOfOrigin = v.countryOfOrigin.toUpperCase();
    for (const k of ["grossWeightKg", "netWeightKg", "lengthCm", "widthCm", "heightCm"] as const) {
      if (!v[k]) continue;
      const n = parseNumber(v[k]);
      if (n == null || n <= 0) issues.push({ line, message: `${PRODUCT_FIELDS[k].label} „${v[k]}“ ist keine Zahl > 0` });
      else f[k] = n;
    }
    // nur ein Gewicht in der Datei → gilt für brutto und netto
    if (f.grossWeightKg != null && f.netWeightKg == null) f.netWeightKg = f.grossWeightKg;
    else if (f.netWeightKg != null && f.grossWeightKg == null) f.grossWeightKg = f.netWeightKg;
    if (v.customsValue) {
      const n = parseNumber(v.customsValue);
      if (n == null || n < 0) issues.push({ line, message: `Zollwert „${v.customsValue}“ ist keine Zahl` });
      else f.customsValueMinor = Math.round(n * 100);
    }
    for (const k of ["batchManaged", "bestBeforeManaged"] as const) {
      const b = parseBool(v[k]);
      if (b !== undefined) f[k] = b;
    }
    out.set(sku, { line, sku, fields: f });
  }
  return { products: [...out.values()], issues };
}
